/** @jsxImportSource @emotion/react */
import React, {useContext} from 'react';
import styled from '@emotion/styled/macro';
import {ReactComponent as ApolloIcon} from './assets/apollo.svg';
import {ReactComponent as FoundationIcon} from './assets/person.svg';
import {ReactComponent as PayrollIcon} from './assets/money.svg';
import {ReactComponent as AttendanceIcon} from './assets/rocket.svg';
import {ReactComponent as Check} from './assets/check.svg';
import {faStar} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {ProgressStatus} from './enums';
import AppContext from 'app/gakki/context/app-context';

const {Disabled, Active, Complete} = ProgressStatus;

interface ModuleItem {
  abbr: string;
  name: string;
  icon?: React.ReactNode;
  items?: SubModuleItem[];
  status?: number;
  step?: number;
}

interface SubModuleItem {
  name: string;
  status: number;
  step?: number;
}

interface Props {
  active?: boolean;
  enablePY?: boolean;
}

interface StatusProps {
  isActive?: boolean;
  isComplete?: boolean;
  isLink?: boolean;
}

const inactiveDatas: ModuleItem[] = [
  {
    abbr: 'FD',
    name: '人事組織',
    icon: <FoundationIcon />,
    items: [
      {
        name: '職稱設定',
        status: 1,
      },
      {
        name: '人員資料',
        status: 1,
      },
      {
        name: '組織資料',
        status: 1,
      },
    ],
  },
  {
    abbr: 'PT',
    name: '行動出勤',
    icon: <AttendanceIcon />,
    status: Active,
    items: [
      {
        name: '上下班/彈性工時',
        status: 1,
      },
      {
        name: '剩餘假別',
        status: 1,
      },
      {
        name: '打卡方式',
        status: 1,
      },
      {
        name: '排班設定',
        status: 1,
      },
    ],
  },
  {
    abbr: 'PY',
    name: '智慧結薪',
    icon: <PayrollIcon />,
    status: Active,
    items: [
      {
        name: '發薪設定',
        status: 1,
      },
      {
        name: '公司設定',
        status: 1,
      },
      {
        name: '員工設定',
        status: 1,
      },
    ],
  },
  {
    abbr: 'XE',
    name: '啟用Apollo XE',
    icon: <ApolloIcon />,
    status: 1,
  },
];
const activeDatas: ModuleItem[] = [
  {
    abbr: 'FD',
    name: '人事組織',
    icon: <FoundationIcon />,
    items: [
      {
        name: '職稱設定',
        status: 2,
        step: 1,
      },
      {
        name: '人員資料',
        status: 2,
        step: 2,
      },
      {
        name: '組織資料',
        status: 2,
        step: 3,
      },
    ],
  },
  {
    abbr: 'PT',
    name: '行動出勤',
    icon: <AttendanceIcon />,
    status: Complete,
    items: [
      {
        name: '上下班/彈性工時',
        status: 2,
        step: 4,
      },
      {
        name: '剩餘假別',
        status: 2,
        step: 5,
      },
      {
        name: '打卡方式',
        status: 2,
        step: 6,
      },
      {
        name: '排班設定',
        status: 2,
        step: 7,
      },
    ],
  },
  {
    abbr: 'PY',
    name: '智慧結薪',
    icon: <PayrollIcon />,
    status: Complete,
    items: [
      {
        name: '發薪設定',
        status: 2,
        step: 8,
      },
      {
        name: '公司設定',
        status: 2,
        step: 9,
      },
      {
        name: '員工設定',
        status: 2,
        step: 10,
      },
    ],
  },
  {
    abbr: 'XE',
    name: '啟用Apollo XE',
    icon: <ApolloIcon />,
    status: 2,
  },
];

const Wrapper = styled.div<StatusProps>`
  position: relative;

  :not(:first-of-type) {
    margin-left: 21px;

    ::before {
      content: '';
      position: absolute;
      margin: 2px 0;
      width: 21px;
      height: 1px;
      background: ${props =>
        props.isComplete ? '#26abe3' : props.isActive ? '#353d45' : '#e9eced'};
      top: 24px;
      left: -21px;
    }
  }
`;
const MainItem = styled.div<StatusProps>`
  width: 220px;
  height: 53px;
  display: flex;
  align-items: center;
  border-radius: 100px;
  border: solid 1px
    ${props =>
      props.isComplete ? '#26abe3' : props.isActive ? '#353d45' : '#e9eced'};
  padding-left: 3px;
`;

const MainIcon = styled.div<StatusProps>`
  color: ${props =>
    props.isComplete ? '#26abe3' : props.isActive ? '#353d45' : '#e9eced'};
  svg {
    height: 49.2px;
    width: 49.2px;
  }
`;

const MainText = styled.div<StatusProps>`
  color: ${props => (props.isActive ? '#353d45' : '#e9eced')};
  margin-left: 10px;
  font-size: 15px;
`;

const SubItem = styled.div<StatusProps>`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding-left: 10px;
  margin-top: 17px;
  position: relative;

  ::before {
    content: '';
    position: absolute;
    left: 30px;
    width: 1px;
    height: 17px;
    top: -17px;
    background: ${props =>
      props.isComplete ? '#26abe3' : props.isActive ? '#353d45' : '#e9eced'};
  }

  &:first-of-type {
    margin-top: 26px;
    ::before {
      height: 26px;
      top: -26px;
    }
  }
`;

const SubIcon = styled.div<StatusProps>`
  width: 40px;
  height: 40px;
  border: solid 1px
    ${props =>
      props.isComplete ? '#26abe3' : props.isActive ? '#353d45' : '#e9eced'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FaIcon = styled.div<StatusProps>`
  background: ${props =>
    props.isComplete ? '#26abe3' : props.isActive ? '#353d45' : '#e9eced'};
  height: 30px;
  width: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SubText = styled.div<StatusProps>`
  width: 155px;
  height: 40px;
  line-height: 38px;
  padding: 0 18px;
  border-radius: 100px;
  font-size: 15px;
  cursor: ${props => (props.isLink ? 'pointer' : 'normal')};
  border: solid 1px
    ${props =>
      props.isComplete ? '#26abe3' : props.isActive ? '#353d45' : '#e9eced'};
  color: ${props =>
    props.isActive || props.isComplete ? '#353d45' : '#e9eced'};
`;

export default function ProgressBoard(props: Props) {
  const {changeStep} = useContext(AppContext);
  const {active, enablePY} = props;

  const datas = active ? activeDatas : inactiveDatas;
  return (
    <div css={{display: 'flex'}}>
      {datas.map((module: ModuleItem, i: number) => {
        if (!enablePY && module.abbr === 'PY') {
          return null;
        }
        const {items, status} = module;
        const isPrevComplete =
          datas[i - 1] &&
          (datas[i - 1].items || []).every(
            (i: SubModuleItem) => i.status === Complete,
          );
        const hasCompleteItem = (datas[i].items || []).some(
          (i: SubModuleItem) => i.status === Complete,
        );
        const isActive =
          (items && items.some((i: SubModuleItem) => i.status !== Disabled)) ||
          status === Active;
        const isComplete =
          (items &&
            items.length > 0 &&
            items.every((i: SubModuleItem) => i.status === Complete)) ||
          status === Complete;
        return (
          <Wrapper
            isActive={isActive}
            isComplete={isComplete || isPrevComplete}
            key={`main_${i}`}
          >
            <MainItem
              isActive={isActive || isPrevComplete}
              isComplete={isComplete || hasCompleteItem}
            >
              <MainIcon
                isActive={isActive || isPrevComplete}
                isComplete={isComplete || hasCompleteItem}
              >
                {module.icon}
              </MainIcon>
              <MainText
                isActive={isActive || isPrevComplete}
                isComplete={isComplete}
              >
                {module.name}
              </MainText>
            </MainItem>
            <div>
              {items &&
                items.map((item: SubModuleItem, subIndex: number) => {
                  const nonComplete = item.status !== Complete;
                  const isActive = item.status === Active;
                  const isComplete = item.status === Complete;
                  const handleClick = () => {
                    if (item.step) {
                      changeStep(item.step);
                    }
                  };
                  return (
                    <SubItem
                      isActive={isActive}
                      isComplete={isComplete}
                      key={`sub_${subIndex}`}
                    >
                      <SubIcon isActive={isActive} isComplete={isComplete}>
                        {nonComplete ? (
                          <FaIcon isActive={isActive} isComplete={isComplete}>
                            <FontAwesomeIcon
                              css={{
                                color: '#FFF',
                                fontSize: '16px',
                              }}
                              icon={faStar}
                            />
                          </FaIcon>
                        ) : (
                          <Check />
                        )}
                      </SubIcon>
                      <SubText
                        isActive={isActive}
                        isComplete={isComplete}
                        isLink={Boolean(item.step)}
                        onClick={handleClick}
                      >
                        {item.name}
                      </SubText>
                    </SubItem>
                  );
                })}
            </div>
          </Wrapper>
        );
      }).filter(t=>t)}
    </div>
  );
}
